import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  Award,
  Copy,
  Download,
  Mail,
  MessageSquare,
  MoreVertical,
  RefreshCw,
  Send,
  Trash2,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { DangerousConfirmDialog } from '@/components/ui/dangerous-confirm-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { PageHeader } from '@/components/ui/page-header';
import { BulkActionBar, BulkButton } from '@/components/data-table/BulkActionBar';
import {
  SelecteurSousSegmentFormation,
  useSousSegmentsFormation,
  type FormationSegmentValue,
} from '@/components/formation/SelecteurSousSegmentFormation';
import {
  exportToCSV,
  useBulkEvaluations,
  useEvaluations,
  useFormations,
  useSessions,
} from '@/hooks/queries/useFormation';
import { useRole } from '@/hooks/useRole';
import { formatDateDZ } from '@/lib/format';
import { cn } from '@/lib/utils';
import { supabase } from '@/lib/supabase';
import type { EvalType, EvaluationFormationRow } from '@/lib/database.types';

type FilterStatut = 'all' | 'completed' | 'pending' | 'expired' | 'draft';

export function EvaluationsPage(): JSX.Element {
  const { isAdmin } = useRole();
  const [segment, setSegment] = useState<FormationSegmentValue>('all');
  const { ids: segmentIds } = useSousSegmentsFormation(segment);
  const [typeFilter, setTypeFilter] = useState<EvalType | 'all'>('all');
  const [statutFilter, setStatutFilter] = useState<FilterStatut>('all');

  const { data: chaud = [], refetch: refetchChaud } = useEvaluations({ type: 'chaud' });
  const { data: froid = [], refetch: refetchFroid } = useEvaluations({ type: 'froid' });
  const { data: formations = [] } = useFormations({ sousSegmentIds: segmentIds });
  const formationIds = useMemo(() => formations.map((f) => f.id), [formations]);
  const { data: sessions = [] } = useSessions({});
  const sessionIdsScoped = useMemo(
    () => sessions.filter((s) => formationIds.includes(s.formation_id)).map((s) => s.id),
    [sessions, formationIds],
  );

  const isDraft = (e: EvaluationFormationRow): boolean => {
    // Brouillon : token_expire_at < now() ET completed_at = null ET token_expire_at proche de l'epoch
    if (e.completed_at) return false;
    return new Date(e.token_expire_at).getFullYear() < 2000;
  };

  const all = useMemo(() => {
    let list: EvaluationFormationRow[] =
      typeFilter === 'chaud' ? chaud : typeFilter === 'froid' ? froid : [...chaud, ...froid];
    if (formationIds.length > 0) list = list.filter((e) => sessionIdsScoped.includes(e.session_id));
    if (statutFilter === 'completed') list = list.filter((e) => e.completed_at);
    else if (statutFilter === 'pending')
      list = list.filter(
        (e) => !e.completed_at && !isDraft(e) && new Date(e.token_expire_at) >= new Date(),
      );
    else if (statutFilter === 'expired')
      list = list.filter(
        (e) => !e.completed_at && !isDraft(e) && new Date(e.token_expire_at) < new Date(),
      );
    else if (statutFilter === 'draft') list = list.filter((e) => isDraft(e));
    return list;
  }, [chaud, froid, typeFilter, statutFilter, formationIds, sessionIdsScoped]);

  const stats = useMemo(() => {
    const completed = all.filter((e) => e.completed_at && e.nps_score != null);
    const npsAvg =
      completed.length > 0
        ? completed.reduce((s, e) => s + (e.nps_score ?? 0), 0) / completed.length
        : null;
    const promoters = completed.filter((e) => (e.nps_score ?? 0) >= 9).length;
    const detractors = completed.filter((e) => (e.nps_score ?? 0) <= 6).length;
    const npsScore =
      completed.length > 0 ? Math.round(((promoters - detractors) / completed.length) * 100) : null;
    const pending = all.filter(
      (e) => !e.completed_at && !isDraft(e) && new Date(e.token_expire_at) >= new Date(),
    ).length;
    const expired = all.filter(
      (e) => !e.completed_at && !isDraft(e) && new Date(e.token_expire_at) < new Date(),
    ).length;
    const draftCount = all.filter((e) => isDraft(e)).length;
    return {
      totalSent: all.length,
      completed: completed.length,
      pending,
      expired,
      draftCount,
      npsAvg,
      npsScore,
    };
  }, [all]);

  const sessionMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const s of sessions) m.set(s.id, s.reference);
    return m;
  }, [sessions]);

  const bulk = useBulkEvaluations();

  // Bulk
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkConfirmDelete, setBulkConfirmDelete] = useState(false);
  const clearSelection = (): void => setSelected(new Set());
  const toggleSelect = (id: string): void => {
    setSelected((cur) => {
      const next = new Set(cur);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const selectAll = (): void => setSelected(new Set(all.map((e) => e.id)));
  const selectedRows = all.filter((e) => selected.has(e.id));

  const handleRenvoyerLien = async (e: EvaluationFormationRow): Promise<void> => {
    const newExpire = new Date(Date.now() + 7 * 86400000).toISOString();
    const { error } = await supabase
      .from('evaluations_formation')
      .update({ token_expire_at: newExpire })
      .eq('id', e.id);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success(`Lien régénéré (+7j) — copiez l'URL : /eval/${e.token}`);
    void refetchChaud();
    void refetchFroid();
  };

  const handleCopierLien = async (e: EvaluationFormationRow): Promise<void> => {
    const url = `${window.location.origin}/eval/${e.token}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Lien copié');
    } catch {
      toast.success(url);
    }
  };

  return (
    <div className="space-y-4 p-6">
      <PageHeader
        title="Évaluations"
        subtitle="Retours à chaud et à froid des participants — alimente les NPS catalogue/formateurs."
        actions={
          <div className="flex items-center gap-2">
            <SelecteurSousSegmentFormation value={segment} onChange={setSegment} />
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value as EvalType | 'all')}
              className="h-9 rounded-md border border-input bg-background px-2 text-sm"
            >
              <option value="all">— Tous types —</option>
              <option value="chaud">À chaud</option>
              <option value="froid">À froid</option>
            </select>
            <select
              value={statutFilter}
              onChange={(e) => setStatutFilter(e.target.value as FilterStatut)}
              className="h-9 rounded-md border border-input bg-background px-2 text-sm"
            >
              <option value="all">— Tous statuts —</option>
              <option value="completed">Complétées</option>
              <option value="pending">En attente</option>
              <option value="expired">Expirées</option>
              <option value="draft">Brouillon</option>
            </select>
          </div>
        }
      />

      <BulkActionBar count={selected.size} onClear={clearSelection}>
        <BulkButton onClick={selectAll}>Tout ({all.length})</BulkButton>
        <BulkButton
          onClick={async () => {
            const r = await bulk.mutateAsync({ ids: [...selected], action: 'regenerer' });
            toast.success(`${r.success} évaluation(s) renvoyée(s) (token +7j)`);
            clearSelection();
            void refetchChaud();
            void refetchFroid();
          }}
        >
          <Send className="h-3.5 w-3.5" /> Envoyer / Renvoyer
        </BulkButton>
        <BulkButton
          onClick={() => {
            exportToCSV(
              selectedRows.map((e) => ({
                type: e.type,
                session: sessionMap.get(e.session_id) ?? '',
                statut: e.completed_at ? 'completee' : isDraft(e) ? 'brouillon' : 'envoyee',
                nps: e.nps_score ?? '',
                note_formateur: e.note_formateur ?? '',
                note_contenu: e.note_contenu ?? '',
                note_logistique: e.note_logistique ?? '',
                commentaire: e.commentaire_libre ?? '',
                date_completion: e.completed_at ?? '',
              })),
              `evaluations-${selectedRows.length}.csv`,
            );
            toast.success(`${selectedRows.length} évaluation(s) exportée(s)`);
          }}
        >
          <Download className="h-3.5 w-3.5" /> Export
        </BulkButton>
        {isAdmin && (
          <BulkButton variant="destructive" onClick={() => setBulkConfirmDelete(true)}>
            <Trash2 className="h-3.5 w-3.5" /> Supprimer
          </BulkButton>
        )}
      </BulkActionBar>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
        <KpiCard
          label="Envoyées"
          value={String(stats.totalSent)}
          icon={<Mail className="h-4 w-4" />}
        />
        <KpiCard
          label="Complétées"
          value={`${stats.completed} / ${stats.totalSent}`}
          subtext={
            stats.totalSent > 0
              ? `${Math.round((100 * stats.completed) / stats.totalSent)}%`
              : undefined
          }
          icon={<MessageSquare className="h-4 w-4" />}
        />
        <KpiCard
          label="En attente"
          value={`${stats.pending}`}
          subtext={`${stats.expired} expirées`}
          icon={<RefreshCw className="h-4 w-4" />}
          tone={stats.expired > 0 ? 'warning' : 'default'}
        />
        <KpiCard
          label="Brouillons"
          value={`${stats.draftCount}`}
          icon={<MessageSquare className="h-4 w-4" />}
        />
        <KpiCard
          label="Score NPS net"
          value={stats.npsScore != null ? String(stats.npsScore) : '—'}
          subtext={stats.npsAvg != null ? `Moyenne ${stats.npsAvg.toFixed(1)} / 10` : undefined}
          icon={<Award className="h-4 w-4" />}
          tone={
            stats.npsScore != null && stats.npsScore >= 30
              ? 'success'
              : stats.npsScore != null && stats.npsScore < 0
                ? 'destructive'
                : 'default'
          }
        />
      </div>

      <section className="rounded-lg border border-border bg-card p-4">
        <table className="w-full text-xs">
          <thead className="text-left text-[10px] uppercase text-muted-foreground">
            <tr>
              <th className="py-2">
                <input
                  type="checkbox"
                  checked={selected.size === all.length && all.length > 0}
                  onChange={() => (selected.size === all.length ? clearSelection() : selectAll())}
                />
              </th>
              <th className="py-2">Type</th>
              <th className="py-2">Session</th>
              <th className="py-2">Statut</th>
              <th className="py-2">Date complétion</th>
              <th className="py-2">NPS</th>
              <th className="py-2">Formateur</th>
              <th className="py-2">Contenu</th>
              <th className="py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {all.slice(0, 100).map((e) => {
              const completed = !!e.completed_at;
              const draft = isDraft(e);
              const expired = !completed && !draft && new Date(e.token_expire_at) < new Date();
              return (
                <tr
                  key={e.id}
                  className={cn('border-b border-border/40', selected.has(e.id) && 'bg-primary/5')}
                >
                  <td className="py-1">
                    <input
                      type="checkbox"
                      checked={selected.has(e.id)}
                      onChange={() => toggleSelect(e.id)}
                    />
                  </td>
                  <td className="py-1">
                    <Badge variant="outline">{e.type === 'chaud' ? 'À chaud' : 'À froid'}</Badge>
                  </td>
                  <td className="py-1 font-mono">{sessionMap.get(e.session_id) ?? '—'}</td>
                  <td className="py-1">
                    {completed ? (
                      <Badge variant="success">Complétée</Badge>
                    ) : draft ? (
                      <Badge variant="secondary">Brouillon</Badge>
                    ) : expired ? (
                      <Badge variant="destructive">Expirée</Badge>
                    ) : (
                      <Badge variant="warning">En attente</Badge>
                    )}
                  </td>
                  <td className="num py-1">
                    {e.completed_at ? formatDateDZ(e.completed_at) : '—'}
                  </td>
                  <td className="py-1">
                    {e.nps_score != null ? (
                      <Badge
                        variant={
                          e.nps_score >= 9
                            ? 'success'
                            : e.nps_score >= 7
                              ? 'default'
                              : 'destructive'
                        }
                      >
                        {e.nps_score}
                      </Badge>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="num py-1">
                    {e.note_formateur != null ? Number(e.note_formateur).toFixed(1) : '—'}
                  </td>
                  <td className="num py-1">
                    {e.note_contenu != null ? Number(e.note_contenu).toFixed(1) : '—'}
                  </td>
                  <td className="py-1 text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-7 w-7">
                          <MoreVertical className="h-3.5 w-3.5" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onSelect={() => void handleCopierLien(e)}>
                          <Copy className="h-3.5 w-3.5" /> Copier le lien
                        </DropdownMenuItem>
                        {!completed && (
                          <DropdownMenuItem onSelect={() => void handleRenvoyerLien(e)}>
                            <RefreshCw className="h-3.5 w-3.5" />
                            {draft ? 'Activer (envoyer)' : 'Régénérer (+7j)'}
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem asChild>
                          <a href={`/eval/${e.token}`} target="_blank" rel="noreferrer">
                            Voir page publique
                          </a>
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </td>
                </tr>
              );
            })}
            {all.length === 0 && (
              <tr>
                <td colSpan={9} className="py-4 text-center text-muted-foreground">
                  Aucune évaluation.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </section>

      <DangerousConfirmDialog
        open={bulkConfirmDelete}
        onOpenChange={setBulkConfirmDelete}
        title={`Supprimer ${selected.size} évaluation${selected.size > 1 ? 's' : ''} ?`}
        description="Action irréversible. Les évaluations complétées contiennent des données analytics."
        consequences={[
          `${selected.size} évaluation(s) effacées`,
          'Les NPS agrégés sur formations/formateurs seront recalculés',
          'Audit log conservé',
        ]}
        challenge="SUPPRIMER"
        loading={bulk.isPending}
        onConfirm={async () => {
          const r = await bulk.mutateAsync({ ids: [...selected], action: 'delete' });
          if (r.errors.length === 0) toast.success(`${r.success} évaluation(s) supprimée(s)`);
          else toast.warning(`${r.success} OK · ${r.errors.length} échec(s)`);
          setBulkConfirmDelete(false);
          clearSelection();
        }}
      />
    </div>
  );
}

function KpiCard({
  label,
  value,
  subtext,
  icon,
  tone,
}: {
  label: string;
  value: string;
  subtext?: string;
  icon?: JSX.Element;
  tone?: 'default' | 'success' | 'warning' | 'destructive';
}): JSX.Element {
  return (
    <div className="rounded-lg border border-border bg-card p-3">
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-wide text-muted-foreground">
        {icon}
        {label}
      </div>
      <div
        className={cn(
          'num text-xl font-semibold',
          tone === 'success' && 'text-success',
          tone === 'warning' && 'text-warning',
          tone === 'destructive' && 'text-destructive',
        )}
      >
        {value}
      </div>
      {subtext && <div className="text-[10px] text-muted-foreground">{subtext}</div>}
    </div>
  );
}
